import { create } from "zustand";

export type ThemeId = "neon-cyber" | "professional-light" | "professional-dark";

export const THEMES: { id: ThemeId; label: string; description: string }[] = [
  { id: "neon-cyber", label: "Neon Cyber", description: "Teal glow, glass surfaces" },
  { id: "professional-light", label: "Professional Light", description: "Clean, enterprise-ready" },
  { id: "professional-dark", label: "Professional Dark", description: "Charcoal, high contrast" },
];

const STORAGE_KEY = "costorah-theme";

function isThemeId(value: string | null): value is ThemeId {
  return value === "neon-cyber" || value === "professional-light" || value === "professional-dark";
}

/** Reads the theme the blocking inline script in index.html already applied to <html>, avoiding a flash. */
function getInitialTheme(): ThemeId {
  if (typeof document !== "undefined") {
    const attr = document.documentElement.getAttribute("data-theme");
    if (isThemeId(attr)) return attr;
  }
  if (typeof window !== "undefined" && window.matchMedia("(prefers-color-scheme: dark)").matches) {
    return "professional-dark";
  }
  return "professional-light";
}

interface ThemeState {
  theme: ThemeId;
  setTheme: (theme: ThemeId) => void;
}

/* EP-25.5 — the whole-page color cross-fade lives behind this class (see
 * index.css's motion architecture) instead of a universal `* { transition }`,
 * so hovers stay crisp during normal use and the 250ms fade only ever runs
 * while an actual theme switch is in flight. */
let themeTransitionTimer: number | undefined;

export const useThemeStore = create<ThemeState>()((set) => ({
  theme: getInitialTheme(),
  setTheme: (theme) => {
    const root = document.documentElement;
    root.classList.add("theme-transitioning");
    root.setAttribute("data-theme", theme);
    if (typeof window !== "undefined") {
      window.clearTimeout(themeTransitionTimer);
      themeTransitionTimer = window.setTimeout(
        () => root.classList.remove("theme-transitioning"),
        350,
      );
    }
    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      // Storage unavailable (private browsing, quota) — theme still applies for this session.
    }
    set({ theme });
  },
}));
